import clsx from "clsx"
import { format, isToday } from "date-fns"

import { ActivityIndicator } from "../../components/ActivityIndicator"
import { TheAnyThingLogo } from "../../components/TheAnyThingLogo"
import { useTimetableStore } from "../timetable/lib/useTimetableStore"

import {
  bracketStyle,
  buttonActiveStyle,
  buttonStyle,
  checkboxGroupStyle,
  checkboxLabelStyle,
  checkboxStyle,
  clockStyle,
  connectionDotStyle,
  connectionDotVariant,
  connectionStyle,
  containerStyle,
  dateStyle,
  emptyStyle,
  errorStyle,
  footerStyle,
  guestStyle,
  headerMetaStyle,
  headerStyle,
  labelStyle,
  listHeaderStyle,
  listInnerStyle,
  listStyle,
  loadingContainerStyle,
  momentCellStyle,
  noMomentStyle,
  overlayButtonStyle,
  overlayStyle,
  overlayTextStyle,
  overlayTitleStyle,
  productStyle,
  roomNameStyle,
  rowFinishedStyle,
  rowRunningStyle,
  rowStyle,
  sliderStyle,
  statusStyle,
  statusVariant,
  timeStyle,
  titleStyle,
  volumeControlStyle,
  volumeValueStyle,
} from "./announcements.css"
import {
  ANNOUNCEMENT_LABELS,
  ANNOUNCEMENT_TYPES,
  Announcement,
  AnnouncementStatus,
  useAnnouncements,
} from "./lib/useAnnouncements"

const statusLabel: Record<AnnouncementStatus, string> = {
  pending: "Waiting",
  queued: "Queued",
  played: "Announced",
  muted: "Muted",
  missed: "Missed",
  off: "Off",
}

function MomentCell({ announcement }: { announcement?: Announcement }) {
  if (!announcement) {
    // Only the film end, and only until the projector has reported it stopped.
    return <span className={noMomentStyle}>&mdash;</span>
  }

  return (
    <div className={momentCellStyle}>
      {/* Only `now` and `next` reach us, so anything not today is tomorrow. The date is shown
          rather than spelled out, so every row reads as a clock time. */}
      {!isToday(announcement.at) && (
        <span className={dateStyle}>{format(announcement.at, "dd/MM")}</span>
      )}
      <span className={timeStyle}>{format(announcement.at, "HH:mm")}</span>
      <span className={clsx(statusStyle, statusVariant[announcement.status])}>
        {statusLabel[announcement.status]}
      </span>
    </div>
  )
}

export default function AnnouncementsContent() {
  const webSocket = useTimetableStore((state) => state.webSocket)
  const isInitialized = useTimetableStore((state) => state.isInitialized)
  const locationId = useTimetableStore((state) => state.params.locationId)
  const locationName = useTimetableStore((state) => state.locationName)

  const {
    bookings,
    now,
    isArmed,
    isSupported,
    error,
    volume,
    isMuted,
    enabled,
    arm,
    test,
    changeVolume,
    toggleMuted,
    toggleEnabled,
  } = useAnnouncements()

  // The overlay is deliberately outside the loading branch. The server sends nothing at all when
  // a location has no bookings, so the list can sit on its spinner all morning - and if arming
  // waited for that, nobody could start the announcements before the first booking appeared.
  const armOverlay = !isArmed && (
    <div className={overlayStyle}>
      <TheAnyThingLogo />
      <h2 className={overlayTitleStyle}>Announcements</h2>
      {isSupported ? (
        <>
          <p className={overlayTextStyle}>
            Browsers only allow audio after a click. Start the announcements once and you can
            minimise this window: it keeps announcing for this location.
          </p>
          <button type="button" className={overlayButtonStyle} onClick={() => void arm()}>
            Start announcements
          </button>
        </>
      ) : (
        <p className={overlayTextStyle}>
          This browser has no Web Audio support, so announcements cannot be played here. Use a
          recent Chrome.
        </p>
      )}
      {error && <p className={errorStyle}>{error}</p>}
    </div>
  )

  if (!isInitialized) {
    return (
      <>
        <div className={loadingContainerStyle}>
          <TheAnyThingLogo />
          <ActivityIndicator color="white" size={64} />
        </div>
        {armOverlay}
      </>
    )
  }

  return (
    <div className={containerStyle}>
      <header className={headerStyle}>
        <h1 className={titleStyle}>
          <span className={bracketStyle}>( </span>
          Announcements
          <span className={bracketStyle}> )</span>
        </h1>
        <p className={headerMetaStyle}>{locationName ?? locationId}</p>
        <p className={clockStyle}>{format(now, "HH:mm:ss")}</p>
        <p className={connectionStyle}>
          <span
            className={clsx(
              connectionDotStyle,
              connectionDotVariant[webSocket.isConnected ? "connected" : "disconnected"],
            )}
          />
          {webSocket.isConnected ? "Live" : "Reconnecting"}
        </p>
      </header>

      {error && <p className={errorStyle}>{error}</p>}

      <div className={listStyle}>
        <div className={listInnerStyle}>
          <div className={listHeaderStyle}>
            <span>Start</span>
            <span>Film end</span>
            <span>End</span>
            <span>Room</span>
            <span>Guest</span>
            <span>Product</span>
          </div>

          {bookings.length === 0 ? (
            <p className={emptyStyle}>No bookings to announce right now</p>
          ) : (
            bookings.map((booking) => (
              <div
                key={booking.bookingId}
                className={clsx(
                  rowStyle,
                  booking.isRunning && rowRunningStyle,
                  booking.end.at.getTime() < now && rowFinishedStyle,
                )}>
                <MomentCell announcement={booking.start} />
                <MomentCell announcement={booking.filmEnd} />
                <MomentCell announcement={booking.end} />
                <span className={roomNameStyle}>{booking.roomName}</span>
                <span className={guestStyle}>{booking.userGivenName}</span>
                <span className={productStyle}>{booking.productName}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <footer className={footerStyle}>
        <div className={checkboxGroupStyle}>
          <span className={labelStyle}>Announce</span>
          {ANNOUNCEMENT_TYPES.map((type) => (
            <label key={type} className={checkboxLabelStyle}>
              <input
                className={checkboxStyle}
                type="checkbox"
                checked={enabled[type]}
                onChange={() => toggleEnabled(type)}
              />
              {ANNOUNCEMENT_LABELS[type]}
            </label>
          ))}
        </div>

        <div className={volumeControlStyle}>
          <label className={labelStyle} htmlFor="announcement-volume">
            Volume
          </label>
          <input
            id="announcement-volume"
            className={sliderStyle}
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(volume * 100)}
            onChange={(event) => changeVolume(Number(event.target.value) / 100)}
          />
          <span className={volumeValueStyle}>{Math.round(volume * 100)}%</span>
        </div>

        <button
          type="button"
          className={clsx(buttonStyle, isMuted && buttonActiveStyle)}
          onClick={toggleMuted}>
          {isMuted ? "Unmute" : "Mute"}
        </button>

        <button type="button" className={buttonStyle} onClick={test}>
          Test audio
        </button>
      </footer>

      {armOverlay}
    </div>
  )
}
